import { getWordpress } from 'PublicAPI';

import { GUIDES_PATH } from '../wordpress';
import { GUIDE_SUMMARY_KEYS } from '../wordpress/@types/guide';

const formatWpData = (body) => {
  const wp = {
    archive: null,
    author: null,
    content: null,
    editor: null,
    meta: {
      canonical: null,
      desc: null,
      title: null,
    },
    posts: [],
    title: null,
  };

  if (body.length > 0) {
    const data = body[0];

    wp.title = data.title.rendered;
    wp.content = data.content.rendered;
    wp.meta = {
      canonical: data?.yoast?.yoast_wpseo_canonical,
      desc: data?.yoast?.yoast_wpseo_metadesc,
      modified: data?.modified,
      published: data?.date,
      title: data?.yoast?.yoast_wpseo_title,
    };
    /* eslint-disable no-underscore-dangle */
    if (data?._embedded?.author?.[0]) {
      const embeddedAuthor = data._embedded.author[0];
      const socialLinks = embeddedAuthor?.yoast_head_json?.schema?.['@graph']?.find(
        (item) => item['@type'] === 'Person',
      )?.sameAs ?? [];

      wp.author = {
        description: embeddedAuthor.description,
        featured_author: embeddedAuthor.acf?.featured_author,
        id: embeddedAuthor.id,
        jobTitle: embeddedAuthor.acf?.job_title ?? '',
        name: embeddedAuthor.name,
        socialLinks,
        thumbnail: embeddedAuthor.acf?.profile_image?.sizes?.thumbnail,
      };
    }
    /* eslint-enable no-underscore-dangle */
    wp.editor = data?.acf?.guide_editor?.display_name;
  }
  return wp;
};

const getPage = async (slug, isGuideRoute, isTestingWpEngineAPI = false, headers = null) => {
  const guide = (isGuideRoute) ? '&parent=32' : '';
  return getWordpress(`/wp/v2/pages/?slug=${slug}${guide}`, isTestingWpEngineAPI, headers);
};

const getBrandPost = async (slug, isTestingWpEngineAPI = false, headers = null) =>
  getWordpress(`/wp/v2/brands/?slug=${encodeURIComponent(slug)}`, isTestingWpEngineAPI, headers);

export const getGuidePage = async ({ route, slug }) => {
  const isGuideRouteV2 = slug.startsWith(GUIDES_PATH);
  const guideSlug = '/' + (route || slug.replace(/^\/sell-my-car/, '')) // eslint-disable-line prefer-template
    .replace(/^\//, '');

  const rootPage = /^\/guides$/.test(guideSlug);

  if (rootPage) {
    const rootSlug = (isGuideRouteV2) ? 'guides-v2' : 'guides';
    const body = await getWordpress(`/wp/v2/pages/?slug=/${rootSlug}`);
    return { ...formatWpData(body), isGuideRouteV2 };
  }

  const parent = (isGuideRouteV2) ? '9041' : '32';
  const body = await getWordpress(`/wp/v2/pages/?slug=${guideSlug}&parent=${parent}&_embed=author,acf`);
  return { ...formatWpData(body), isGuideRouteV2 };
};

export const fetchPageData = async (slug, isGuideRoute = false, isTestingWpEngineAPI = false, headers = null) => {
  const body = await getPage(slug, isGuideRoute, isTestingWpEngineAPI, headers); // https://github.com/motorway/motorway-seller-webapp#wordpress-content
  return formatWpData(body);
};

export const getGuidesPreviewData = async () => {
  const guideData = getWordpress('/wp/v2/pages/?parent=32&orderby=menu_order&order=asc');
  return guideData.then((guides) =>
    guides.map((guide) =>
      Object.fromEntries(Object.entries(guide).filter(([k]) => GUIDE_SUMMARY_KEYS.includes(k)))));
};

// Shared ACF-page formatter for page-builder documents (landing pages and brands).
// Both return the same `{ title, meta, content }` shape where `content` is the ACF object
// that PageBuilder.tsx maps over (`content.page_builder`).
const formatAcfPage = (body) => {
  const wp = {
    archive: null,
    content: null,
    meta: {
      canonical: null,
      desc: null,
      noindex: false,
      title: null,
    },
    posts: [],
    title: null,
  };

  if (!body[0]) {
    return wp;
  }

  const { acf, yoast, yoast_head_json: yoastHead } = body[0];

  const seoTitle = yoastHead?.title || acf.seo_title;
  const seoDescription = yoastHead?.description || acf.seo_description || acf.header_description;
  const noindex = yoast?.['yoast_wpseo_meta-robots-noindex'] === '1';

  const content = {
    ...acf,
    ...(seoTitle && { seo_title: seoTitle }),
    ...(seoDescription && { seo_description: seoDescription }),
  };

  wp.title = acf.header_title || '';
  wp.meta.title = seoTitle || wp.title;
  wp.meta.canonical = yoast?.yoast_wpseo_canonical;
  wp.meta.description = seoDescription || '';
  wp.meta.desc = wp.meta.description;
  wp.meta.noindex = noindex;
  wp.content = content;

  return wp;
};

export const fetchLandingPage = async (slug, isV2WPPage = false) => {
  const pageSlug = isV2WPPage ? `${slug}-v2` : slug;
  const body = await getPage(pageSlug);

  return formatAcfPage(body);
};

export const fetchBrandPage = async (slug, isTestingWpEngineAPI = false, headers = null) => {
  const body = await getBrandPost(slug, isTestingWpEngineAPI, headers);

  return formatAcfPage(body);
};
